import {
  addDoc,
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '../platform/firebase/clients';
import { TaskActivity, TaskActivityKind, TaskStatus } from '../types';
import { wrapSubscriptionError } from './subscriptionError';

const ACTIVITY = 'taskActivity';
const TASK_IDS_PER_QUERY = 30;

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function mapActivity(id: string, data: Record<string, any>): TaskActivity {
  return {
    id,
    taskId: data.taskId ?? '',
    taskTitle: data.taskTitle ?? '',
    actorId: data.actorId ?? '',
    actorName: data.actorName ?? '',
    ...(data.reviewerId !== undefined ? { reviewerId: data.reviewerId } : {}),
    kind: (data.kind ?? 'created') as TaskActivityKind,
    fromStatus: (data.fromStatus ?? null) as TaskStatus | null,
    toStatus: (data.toStatus ?? null) as TaskStatus | null,
    createdAt: toDate(data.createdAt),
  };
}

export async function appendActivity(
  taskId: string,
  taskTitle: string,
  kind: TaskActivityKind,
  fromStatus: TaskStatus | null,
  toStatus: TaskStatus | null,
  user: FirebaseUser,
): Promise<void> {
  await addDoc(collection(db, ACTIVITY), {
    taskId,
    taskTitle,
    actorId: user.uid,
    actorName: user.displayName ?? user.email ?? 'Unknown',
    kind,
    fromStatus,
    toStatus,
    createdAt: serverTimestamp(),
  });
}

export function subscribeToActivity(
  taskId: string,
  onChange: (activity: TaskActivity[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, ACTIVITY),
    where('taskId', '==', taskId),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapActivity(d.id, d.data()))),
    wrapSubscriptionError('taskActivity.subscribeToActivity', onError),
  );
}

/**
 * Reads the append-only activity history for a set of tasks. Activity records
 * intentionally carry taskId rather than campaignId, so the admin campaign
 * report first reads the campaign's tasks and then batches task ids into
 * Firestore's `in` query limit.
 */
export async function listActivityForTasks(taskIds: string[]): Promise<TaskActivity[]> {
  const ids = Array.from(new Set(taskIds.filter(Boolean)));
  if (ids.length === 0) return [];

  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += TASK_IDS_PER_QUERY) {
    chunks.push(ids.slice(index, index + TASK_IDS_PER_QUERY));
  }

  const snapshots = [];
  for (let index = 0; index < chunks.length; index += 4) {
    const batch = await Promise.all(chunks.slice(index, index + 4).map((chunk) => getDocs(
      query(collection(db, ACTIVITY), where('taskId', 'in', chunk)),
    )));
    snapshots.push(...batch);
  }
  return snapshots.flatMap((snapshot) => snapshot.docs.map((document) => mapActivity(document.id, document.data())));
}
